<template>
  <div class="heatmap-chart">
    <div class="heatmap-header">
      <h3>{{ title }}</h3>
      <div class="metric-legend">
        <span class="legend-item">
          <span class="legend-color low"></span>
          Low ({{ thresholds.low }}-{{ thresholds.medium }}{{ unit }})
        </span>
        <span class="legend-item">
          <span class="legend-color medium"></span>
          Medium ({{ thresholds.medium }}-{{ thresholds.high }}{{ unit }})
        </span>
        <span class="legend-item">
          <span class="legend-color high"></span>
          High ({{ thresholds.high }}{{ unit }}+)
        </span>
      </div>
    </div>
    
    <BuiSpinner v-if="isLoading" inline :message="`Loading ${metricType} data...`" />
    
    <div v-else-if="error" class="error-container">
      <p class="error-message">{{ error }}</p>
      <button @click="$emit('retry')" class="retry-button">Retry</button>
    </div>
    
    <div v-else-if="processedData.length === 0" class="no-data-container">
      <p>No {{ metricType }} data available for the selected time range</p>
    </div>
    
    <div v-else class="heatmap-container">
      <div class="heatmap-grid" ref="heatmapGrid">
        <!-- Time axis labels -->
        <div class="time-axis">
          <div 
            v-for="timeLabel in timeLabels" 
            :key="timeLabel.timestamp"
            :class="['time-label', timeLabel.align || 'center']"
            :style="{ left: timeLabel.position + '%' }"
          >
            {{ timeLabel.label }}
          </div>
        </div>
        
        <!-- Player rows -->
        <div class="player-rows">
          <div 
            v-for="player in uniquePlayers" 
            :key="player"
            class="player-row"
          >
            <div class="player-label">
              <a 
                v-if="getPlayerLink(player)"
                :href="getPlayerLink(player)"
                target="_blank"
                class="player-link"
              >
                {{ getPlayerDisplayName(player) }}
              </a>
              <span v-else class="player-name">
                {{ getPlayerDisplayName(player) }}
              </span>
            </div>
            <div class="metric-cells">
              <div
                v-for="cell in getPlayerCells(player)"
                :key="`${player}-${cell?.timestamp || 'unknown'}`"
                class="metric-cell"
                :class="getIntensityClass(cell?.value || 0)"
                :style="{ 
                  left: (cell?.position || 0) + '%',
                  width: (cell?.width || 0) + '%'
                }"
                :title="formatTooltip(player, cell)"
              >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import BuiSpinner from '@/components/bui/atoms/BuiSpinner/BuiSpinner.vue';

export interface HeatmapDataPoint {
  timestamp: string;
  playerUuid: string;
  value: number;
}

interface TimeLabel {
  timestamp: string;
  label: string;
  position: number;
  align: string;
}

interface PlayerRecord {
  name: string;
  id?: number;
  deviceUuid?: string;
}

type PlayerRecordsByUuid = Record<string, PlayerRecord>;

interface Props {
  data: HeatmapDataPoint[];
  metricType: 'mem' | 'cpu' | 'disk' | 'temp';
  isLoading?: boolean;
  error?: string;
  startDate?: Date;
  endDate?: Date;
  playerRecords?: PlayerRecordsByUuid; // Map of playerUuid to player record
  debug?: boolean; // Enable verbose logging
  interval?: '1h' | '4h' | '6h' | '1d'; // Optional: server bucket interval
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  error: '',
  debug: false,
});

// const emit = defineEmits<{
//   retry: [];
// }>();

const heatmapGrid = ref<HTMLElement>();

// Debug logging helpers (silenced unless props.debug is true)
function debugLog(...args: unknown[]) {
  if (props.debug) console.log(...args);
}
function debugWarn(...args: unknown[]) {
  if (props.debug) console.warn(...args);
}

function isValidDataPoint(d: HeatmapDataPoint): boolean {
  const hasValidValue = d.value !== null && d.value !== undefined && !isNaN(d.value);
  const hasValidTimestamp = typeof d.timestamp === 'string' && d.timestamp.length > 0;
  const hasValidUuid = typeof d.playerUuid === 'string' && d.playerUuid.length > 0;
  return hasValidValue && hasValidTimestamp && hasValidUuid;
}

function isValidTimeRange(range: { start: Date; end: Date }): boolean {
  return !!(range.start && range.end && !isNaN(range.start.getTime()) && !isNaN(range.end.getTime()));
}

function logFilteredOutData(dataTimestamp: Date, range: { start: Date; end: Date }): void {
  if (!props.debug) return;
  const dataDateStr = dataTimestamp.toISOString().split('T')[0];
  const dataTimeStr = dataTimestamp.toISOString().split('T')[1].split('.')[0];
  const rangeStartStr = range.start.toISOString();
  const rangeEndStr = range.end.toISOString();
  console.log(`🚫 Filtering out ${dataDateStr} ${dataTimeStr} data (outside range ${rangeStartStr} to ${rangeEndStr})`);
}

function debugProcessedData(filtered: HeatmapDataPoint[], range: { start: Date; end: Date }): void {
  if (!props.debug) return;
  const validValueCount = props.data.filter(d => d.value !== null && d.value !== undefined && !isNaN(d.value)).length;
  const validTimestampCount = props.data.filter(d => d.timestamp && d.timestamp.length > 0).length;
  const validUuidCount = props.data.filter(d => d.playerUuid && d.playerUuid.length > 0).length;
  debugLog(`🔄 Data filtering results:`);
  debugLog(`  • Total input data: ${props.data.length}`);
  debugLog(`  • Valid values: ${validValueCount}`);
  debugLog(`  • Valid timestamps: ${validTimestampCount}`);
  debugLog(`  • Valid UUIDs: ${validUuidCount}`);
  debugLog(`  • Final filtered data: ${filtered.length}/${props.data.length} points`);
  debugLog(`  • Date range filter: ${range.start.toISOString().split('T')[0]} to ${range.end.toISOString().split('T')[0]}`);

  if (filtered.length > 0) {
    const timestamps = filtered.map((d: HeatmapDataPoint) => new Date(d.timestamp));
    const minTime = new Date(Math.min(...timestamps.map((t: Date) => t.getTime())));
    const maxTime = new Date(Math.max(...timestamps.map((t: Date) => t.getTime())));
    debugLog(`📊 Filtered data time range: ${minTime.toISOString()} to ${maxTime.toISOString()}`);
    debugLog(`📊 Filtered data date range: ${minTime.toISOString().split('T')[0]} to ${maxTime.toISOString().split('T')[0]}`);
    const uniqueDates = [...new Set(filtered.map((d: HeatmapDataPoint) => new Date(d.timestamp).toISOString().split('T')[0]))].sort();
    debugLog(`📅 Unique dates in filtered data: ${uniqueDates.join(', ')}`);
  } else {
    debugLog(`📊 No data remaining after filtering`);
  }

  if (props.data.length > 0) {
    const allDates = [...new Set(props.data.map(d => {
      try {
        return new Date(d.timestamp).toISOString().split('T')[0];
      } catch {
        return 'invalid';
      }
    }))].sort();
    debugLog(`📅 All dates in raw input data: ${allDates.join(', ')}`);
    console.log(`🕰️ Sample raw timestamps:`);
    props.data.slice(0, 5).forEach((d, i) => {
      try {
        const ts = new Date(d.timestamp);
        console.log(`  ${i + 1}. ${ts.toISOString()} (${ts.toISOString().split('T')[0]} ${ts.toISOString().split('T')[1].split('.')[0]})`);
      } catch (e) {
        console.log(`  ${i + 1}. Invalid: ${d.timestamp}`);
      }
    });
  }

  const realtimeTestData = filtered.filter((d: HeatmapDataPoint) => d.playerUuid === '805fb6f4-0a03-4903-93b3-d95ad01f9c8f');
  if (realtimeTestData.length > 0) {
    console.log(`🎯 Realtime-logs-test device (805fb6f4-0a03-4903-93b3-d95ad01f9c8f) data:`);
    const dates = [...new Set(realtimeTestData.map((d: HeatmapDataPoint) => new Date(d.timestamp).toISOString().split('T')[0]))].sort();
    console.log('  • Dates found:', dates);
    console.log('  • Total points:', realtimeTestData.length);
    const dateGroups: Record<string, unknown[]> = {};
    realtimeTestData.forEach((d: HeatmapDataPoint) => {
      const date = new Date(d.timestamp).toISOString().split('T')[0];
      if (!dateGroups[date]) dateGroups[date] = [];
      dateGroups[date].push(d);
    });
    console.log('  • Data distribution by date:');
    Object.keys(dateGroups).sort().forEach(date => {
      console.log(`    • ${date}: ${dateGroups[date].length} points`);
    });
  }
}

const title = computed(() => {
  const titles = {
    mem: 'Memory Usage',
    cpu: 'CPU Usage', 
    disk: 'Disk Usage',
    temp: 'Temperature'
  };
  return titles[props.metricType];
});

const unit = computed(() => {
  const units = {
    mem: '%',
    cpu: '%',
    disk: '%',
    temp: '°C'
  };
  return units[props.metricType];
});

const processedData = computed((): HeatmapDataPoint[] => {
  // Early return if no data
  if (!props.data || props.data.length === 0) {
    debugLog('🔄 No data provided to heatmap');
    return [];
  }
  
  const range: { start: Date; end: Date } = timeRange.value;
  
  // Check if we have a valid time range
  if (!isValidTimeRange(range)) {
    debugWarn('⚠️ Invalid time range, showing all data');
    return props.data.filter((d: HeatmapDataPoint) => isValidDataPoint(d));
  }
  
  const filtered: HeatmapDataPoint[] = props.data.filter((d: HeatmapDataPoint) => {
    // Basic validation first
    if (!isValidDataPoint(d)) {
      return false;
    }
    
    // Always filter by date range when we have valid range and props
    try {
      const dataTimestamp = new Date(d.timestamp);
      
      // Check if timestamp is valid
      if (isNaN(dataTimestamp.getTime())) {
        debugWarn(`⚠️ Invalid timestamp: ${d.timestamp}`);
        return false;
      }
      
      // Apply date range filtering
      const isWithinRange: boolean = dataTimestamp >= range.start && dataTimestamp <= range.end;
      if (!isWithinRange) {
        logFilteredOutData(dataTimestamp, range);
      }
      
      return isWithinRange;
    } catch (error) {
      debugWarn(`⚠️ Error processing timestamp ${d.timestamp}:`, error);
      return false;
    }
  });
  
  // Consolidated debug logging
  debugProcessedData(filtered, range);
  
  return filtered;
});

const uniquePlayers = computed(() => {
  const players = [...new Set(processedData.value.map(d => d.playerUuid))];
  return players.sort();
});

const timeRange = computed((): { start: Date; end: Date } => {
  // Use user-selected date range instead of data-driven range
  if (props.startDate && props.endDate) {
    try {
      // Validate that we have proper Date objects
      const startTime = props.startDate instanceof Date ? props.startDate : new Date(props.startDate);
      const endTime = props.endDate instanceof Date ? props.endDate : new Date(props.endDate);
      
      // Check if dates are valid
      if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
        console.error('❌ Invalid dates provided to heatmap:', { startDate: props.startDate, endDate: props.endDate });
        return { start: new Date(), end: new Date() };
      }
      
      // Check if this is a single local-calendar day range
      const isSameDay = (
        startTime.getFullYear() === endTime.getFullYear() &&
        startTime.getMonth() === endTime.getMonth() &&
        startTime.getDate() === endTime.getDate()
      );
      
      let start, end;
      
      // Align to full local days for both single and multi-day ranges
      start = new Date(startTime);
      start.setHours(0, 0, 0, 0); // Start of day (local)
      
      end = new Date(endTime);
      end.setHours(23, 59, 59, 999); // End of day (local)
      
      debugLog('  • Original start:', startTime.toISOString());
      debugLog('  • Original end:', endTime.toISOString());
      debugLog('  • Adjusted start (local->ISO):', start.toISOString());
      debugLog('  • Adjusted end (local->ISO):', end.toISOString());
      debugLog('  • Duration:', {
        hours: Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60)),
        days: Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)),
        isSameDay
      });
      
      return { start, end };
    } catch (error) {
      if (props.debug) console.error('❌ Error calculating time range:', error);
      return { start: new Date(), end: new Date() };
    }
  }
  
  // Fallback to current time if no processed data
  if (processedData.value.length === 0) {
    debugLog('📊 No processed data available, using current time as fallback');
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    return { start: startOfDay, end: endOfDay };
  }
  // Fallback to data-driven range if no date props provided
  try {
    const timestamps = processedData.value.map((d: HeatmapDataPoint) => new Date(d.timestamp)).filter((t: Date) => !isNaN(t.getTime()));
    
    if (timestamps.length === 0) {
      debugWarn('⚠️ No valid timestamps in data, using current time as fallback');
      const now = new Date();
      const startOfDay = new Date(now);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      return { start: startOfDay, end: endOfDay };
    }
    
    const dataStart = new Date(Math.min(...timestamps.map((t: Date) => t.getTime())));
    const dataEnd = new Date(Math.max(...timestamps.map((t: Date) => t.getTime())));
    
    // Align to full local days
    dataStart.setHours(0, 0, 0, 0);
    dataEnd.setHours(23, 59, 59, 999);
    
    debugLog('📊 Using data-driven date range for heatmap (local-day aligned):');
    debugLog('  • Start:', dataStart.toISOString());
    debugLog('  • End:', dataEnd.toISOString());
    
    return { start: dataStart, end: dataEnd };
  } catch (error) {
    console.error('❌ Error calculating data-driven time range:', error);
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setUTCHours(0, 0, 0, 0);
    const endOfDay = new Date(now);
    endOfDay.setUTCHours(23, 59, 59, 999);
    return { start: startOfDay, end: endOfDay };
  }
});

// Hardcoded sensible thresholds for better readability
const thresholds = computed(() => {
  switch (props.metricType) {
    case 'cpu':
      return { low: 0, medium: 30, high: 70, max: 100 };
    case 'mem':
      return { low: 0, medium: 50, high: 80, max: 100 };
    case 'disk':
      return { low: 0, medium: 70, high: 90, max: 100 };
    case 'temp':
      return { low: 0, medium: 50, high: 70, max: 100 };
    default:
      return { low: 0, medium: 50, high: 75, max: 100 };
  }
});

// const minValue = computed(() => thresholds.value.low);
// const maxValue = computed(() => thresholds.value.max);

const timeLabels = computed(() => {
  const range = timeRange.value;
  
  // Check if we have a valid time range
  if (!isValidTimeRange(range)) {
    console.warn('⚠️ Invalid time range for labels, returning empty labels');
    return [];
  }
  
  const duration = range.end.getTime() - range.start.getTime();
  const labels: TimeLabel[] = [];
  
  // Check if this is a single day (less than 24 hours) for hourly labels
  const isSingleDay = duration <= (24 * 60 * 60 * 1000);
  
  debugLog('🕐 Time labels calculation:', {
    rangeStart: range.start.toISOString(),
    rangeEnd: range.end.toISOString(),
    duration: duration / (1000 * 60 * 60) + ' hours',
    isSingleDay
  });
  
  if (isSingleDay) {
    // For single day: show labels every 4 hours starting at 4AM (no left 12AM label)
    // Tick marks at bucket boundaries: 4,8,12,16,20,24
    const hourIntervals = [4, 8, 12, 16, 20, 24];
    
    debugLog('🕐 Single day label positioning:');
    debugLog(`  • Range start: ${range.start.toISOString()}`);
    debugLog(`  • Range end: ${range.end.toISOString()}`);
    debugLog(`  • Duration: ${duration / (1000 * 60 * 60)} hours`);
    
    hourIntervals.forEach(hour => {
      try {
        // Create timestamp for this hour within the day (local time)
        const timestamp = new Date(range.start);
        timestamp.setHours(hour, 0, 0, 0);
        
        // Check if the resulting timestamp is valid
        if (isNaN(timestamp.getTime())) {
          console.warn(`⚠️ Invalid timestamp created for hour ${hour}`);
          return;
        }
        
        // Use the EXACT same positioning calculation as data points across full width
        const rawPosition = ((timestamp.getTime() - range.start.getTime()) / duration) * 100;
        const adjustedPosition = Math.max(0, Math.min(100, rawPosition));
        
        debugLog(`  • Hour ${hour}: ${timestamp.toISOString()} => raw=${rawPosition.toFixed(1)}%, final=${adjustedPosition.toFixed(1)}%`);
        
        // Only add if within the actual time range
        if (timestamp >= range.start && timestamp <= range.end) {
          labels.push({
            timestamp: timestamp.toISOString(),
            label: formatHourLabel(timestamp),
            position: adjustedPosition,
            align: adjustedPosition === 0 ? 'left' : (adjustedPosition === 100 ? 'right' : 'center')
          });
        }
      } catch (error) {
        console.warn(`⚠️ Error creating hourly label for hour ${hour}:`, error);
      }
    });
  } else {
    // For multi-day: show daily labels aligned to local midnight boundaries
    try {
      const startDate = new Date(range.start);
      startDate.setHours(0, 0, 0, 0); // Start at local midnight of first day
      
      const endDate = new Date(range.end);
      endDate.setHours(23, 59, 59, 999); // End at end of last day (local)
      
      // Validate the dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        debugWarn('⚠️ Invalid start or end date for multi-day labels');
        return labels;
      }
      
      // Generate labels for each day centered at local noon to match bucket centering
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        try {
          // Check if current date is valid
          if (isNaN(currentDate.getTime())) {
            debugWarn(`⚠️ Invalid date encountered in label generation: ${currentDate}`);
            break;
          }
          
          // Use noon for label position (center of the day)
          const labelDate = new Date(currentDate);
          labelDate.setHours(12, 0, 0, 0);
          // Position this date within the time range
          const rawPosition = ((labelDate.getTime() - range.start.getTime()) / duration) * 100;
          const adjustedPosition = Math.max(0, Math.min(100, rawPosition));
          
          // Only add if position is within bounds  
          if (rawPosition >= 0 && rawPosition <= 100) {
            labels.push({
              timestamp: currentDate.toISOString(),
              label: formatTimeLabel(currentDate),
              position: adjustedPosition,
              align: adjustedPosition === 0 ? 'left' : (adjustedPosition === 100 ? 'right' : 'center')
            });
          }
          
          // Move to next day (local)
          currentDate.setDate(currentDate.getDate() + 1);
          currentDate.setHours(0, 0, 0, 0); // Ensure we're at local midnight
          
          // Safety check to prevent infinite loops
          if (labels.length > 50) {
            console.warn('⚠️ Too many labels generated, breaking loop');
            break;
          }
        } catch (error) {
          console.warn(`⚠️ Error in daily label generation:`, error);
          break;
        }
      }
    } catch (error) {
      console.warn('⚠️ Error in multi-day label generation:', error);
    }
  }
  
  debugLog('🏷️ Generated time labels:', labels.map(l => ({
    label: l.label,
    position: l.position.toFixed(1) + '%',
    timestamp: l.timestamp
  })));
  
  return labels;
});

function getPlayerCells(playerUuid: string) {
  if (!playerUuid) {
    console.warn('⚠️ No playerUuid provided to getPlayerCells');
    return [];
  }
  
  const playerData = processedData.value
    .filter((d: HeatmapDataPoint) => d.playerUuid === playerUuid)
    .sort((a: HeatmapDataPoint, b: HeatmapDataPoint) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  
  const range = timeRange.value;
  
  // Check if we have a valid time range
  if (!isValidTimeRange(range)) {
    console.warn(`⚠️ Invalid time range for player ${playerUuid.substring(0, 8)}, returning empty cells`);
    return [];
  }
  
  const totalDuration = range.end.getTime() - range.start.getTime();
  const isSingleDay = totalDuration <= (24 * 60 * 60 * 1000);
  const daysDiff = Math.ceil(totalDuration / (1000 * 60 * 60 * 24));
  
  debugLog(`📊 Player ${playerUuid.substring(0, 8)}... positioning analysis:`);
  debugLog(`  • Data points: ${playerData.length}`);
  debugLog(`  • Time span: ${daysDiff} days (${(totalDuration / (1000 * 60 * 60)).toFixed(1)} hours)`);
  debugLog(`  • Single day mode: ${isSingleDay}`);
  debugLog(`  • Range: ${range.start.toISOString()} to ${range.end.toISOString()}`);
  
  if (props.debug && isSingleDay && playerData.length > 0) {
    debugLog(`  • Sample data timestamps:`);
    playerData.slice(0, 3).forEach((point: HeatmapDataPoint, i: number) => {
      const ts = new Date(point.timestamp);
      const hour = ts.getHours();
      const minute = ts.getMinutes();
      debugLog(`    ${i + 1}. ${ts.toString()} (${hour}:${minute.toString().padStart(2, '0')} local)`);
    });
  }
  
  if (playerData.length === 0) {
    if (props.debug) debugLog(`  • No data points for this player`);
    return [];
  }
  
  // Prevent overlaps by tracking the last end position per player row
  let lastEndPosition = -Infinity;
  const cells = playerData.map((point: HeatmapDataPoint) => {
    try {
      const timestamp = new Date(point.timestamp);
      
      // Check if timestamp is valid
      if (isNaN(timestamp.getTime())) {
        debugWarn(`⚠️ Invalid timestamp for player data: ${point.timestamp}`);
        return null;
      }
      
      // Use the EXACT same positioning calculation as time labels
      // Note this is raw position, to be finalized later in position calculation
      const rawPosition = ((timestamp.getTime() - range.start.getTime()) / totalDuration) * 100;
      let position = Math.max(0, Math.min(100, rawPosition));
      // If backend buckets are centered (e.g., 4h buckets at 02,06,10...), adjust to center under label
      // For single day with 4h buckets, we keep ticks at boundaries (0,4,8,...)
      // Cells should start at the boundary, so no centering shift is applied.
      
      // Improved width calculation to prevent overflow
      let finalWidth: number;
      if (isSingleDay) {
        // Exact bucket width for single-day so ends align exactly with labels
        const bucketHours = props.interval === '4h' ? 4 : props.interval === '1h' ? 1 : 1;
        finalWidth = (bucketHours / 24) * 100;
      } else {
        const availableSpace = 100; // percent
        const baseWidth = Math.max(1.5, availableSpace / Math.max(daysDiff * 2, 8));
        const adaptiveWidth = Math.min(baseWidth, availableSpace / Math.max(playerData.length * 1.2, 4));
        finalWidth = Math.max(1.5, Math.min(adaptiveWidth, props.interval === '1d' ? (100 / Math.max(daysDiff, 1)) * 0.6 : 6));
      }
      
      // Ensure position + width doesn't exceed 95%
      const maxPosition = 100 - finalWidth;
      let constrainedPosition = Math.max(0, Math.min(maxPosition, position));
      
      // For single day, disable overlap shifting entirely – exact width handles it
      if (!isSingleDay && props.interval === '1d') {
        const daySpanPct = 100 / Math.max(daysDiff, 1);
        const dayIndex = Math.floor(((timestamp.getTime() - range.start.getTime()) / totalDuration) * Math.max(daysDiff, 1));
        const dayStartPct = daySpanPct * dayIndex;
        constrainedPosition = Math.min(maxPosition, dayStartPct + (daySpanPct / 2) - (finalWidth / 2));
      }
      // Shift right only for multi-day overlaps; allow exact touching for single day
      if (!isSingleDay && constrainedPosition < lastEndPosition) {
        constrainedPosition = Math.min(maxPosition, lastEndPosition);
      }
      lastEndPosition = constrainedPosition + finalWidth;
      
      // Debug individual cell positioning with more detail for single day
      if (props.debug && isSingleDay) {
        const hour = timestamp.getHours();
        const minute = timestamp.getMinutes();
        debugLog(`    • ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} local: raw=${rawPosition.toFixed(1)}%, final=${constrainedPosition.toFixed(1)}%, width=${finalWidth.toFixed(1)}%`);
      } else {
        if (props.debug) debugLog(`    • ${timestamp.toISOString().split('T')[0]} ${timestamp.toISOString().split('T')[1].split('.')[0]}: raw=${rawPosition.toFixed(1)}%, final=${constrainedPosition.toFixed(1)}%, width=${finalWidth.toFixed(1)}%`);
      }
      
      return {
        timestamp: point.timestamp,
        playerUuid: point.playerUuid, // Include playerUuid for tooltip
        value: point.value,
        position: constrainedPosition,
        width: finalWidth
      };
    } catch (error) {
      debugWarn(`⚠️ Error processing data point for player ${playerUuid}:`, error, point);
      return null;
    }
  }).filter((cell: any) => cell !== null); // Remove any null cells from errors
  
  if (props.debug && cells.length > 0) {
    debugLog(`  ✓ Final positioning summary:`);
    debugLog(`    • Position range: ${cells[0]?.position.toFixed(1)}% to ${cells[cells.length - 1]?.position.toFixed(1)}%`);
    debugLog(`    • Last cell ends at: ${((cells[cells.length - 1]?.position || 0) + (cells[cells.length - 1]?.width || 0)).toFixed(1)}%`);
  }
  
  return cells;
}

function getIntensityClass(value: number): string {
  const t = thresholds.value;
  
  if (value <= t.medium) return 'intensity-low';
  if (value <= t.high) return 'intensity-medium';
  return 'intensity-high';
}

function getPlayerDisplayName(playerUuid: string): string {
  // Use player name if available, otherwise fallback to UUID
  const name = props.playerRecords?.[playerUuid]?.name;
  if (name) return name;
  // Fallback: show first 8 characters of UUID
  return playerUuid.substring(0, 8) + '...';
}

function getPlayerLink(playerUuid: string): string | undefined {
  // Generate link to player edit page if we have the player ID
  const id = props.playerRecords?.[playerUuid]?.id;
  if (id !== undefined) return `/players/edit/${id}`;
  return undefined;
}


function formatTimeLabel(date: Date): string {
  // Format as M/D (e.g., "09/22") with leading zeros for better alignment
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}`;
}

function formatHourLabel(date: Date): string {
  // Format as 12AM, 4AM, etc. for hourly display (local time)
  const hours = date.getHours();
  
  if (hours === 0 || hours === 24) {
    return '12AM';
  } else if (hours === 12) {
    return '12PM';
  } else if (hours < 12) {
    return `${hours}AM`;
  } else {
    return `${hours - 12}PM`;
  }
}

function formatTooltip(defaultPlayerUuid: string, cell: any): string {
  const displayName = getPlayerDisplayName(cell.playerUuid || defaultPlayerUuid);
  const valueLine = `${(props.metricType || 'metric').toUpperCase()}: ${Math.round(cell.value)}${unit.value}`;
  // When interval is 1d, show only the date
  if (props.interval === '1d') {
    const d = new Date(cell.timestamp);
    const dateOnly = d.toLocaleDateString();
    return `${displayName}\n${dateOnly}\n${valueLine}`;
  }
  // For sub-daily intervals, show the bucket range (e.g., 8–9 AM)
  const start = new Date(cell.timestamp);
  const bucketHours = props.interval === '6h' ? 6 : props.interval === '4h' ? 4 : 1;
  const end = new Date(start.getTime() + bucketHours * 60 * 60 * 1000);
  const fmt = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${displayName}\n${fmt(start)} – ${fmt(end)}\n${valueLine}`;
}
</script>

<style scoped>
.heatmap-chart {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.heatmap-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 1rem;
}

.heatmap-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--bui-tbl-row-text, #1f2937);
}

.metric-legend {
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.legend-color.low {
  background: #BAFF5C;
}

.legend-color.medium {
  background: #00FFFF;
}

.legend-color.high {
  background: #DE3DA9;
}

.error-container,
.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: var(--bui-tbl-detail-text, #6b7280);
}

.error-message {
  color: #dc2626;
  margin-bottom: 1rem;
}

.retry-button {
  padding: 0.5rem 1rem;
  background: var(--bui-cta-sec-fill, #4b1886);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-button:hover {
  filter: brightness(1.08); background: var(--bui-cta-sec-fill, #4b1886);
}

.heatmap-container {
  flex: 1;
  padding: 0.5rem 1rem 0.5rem 1rem; /* Reduced top/bottom padding */
  overflow: auto;
}

.heatmap-grid {
  position: relative;
  min-height: 200px; /* Reduced from 300px */
  background: var(--bui-ter-input-fill, #f9fafb);
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.time-axis {
  position: relative;
  height: 30px;
  border-bottom: 1px solid #e5e7eb;
  background: var(--bui-tbl-row-fill, #fffcfd);
  margin-left: 100px; /* Align with metric cells */
  padding: 0; /* Use full width for labels */
  width: calc(100% - 100px); /* Match metric cells width */
  overflow: hidden; /* Prevent overflow */
  box-sizing: border-box; /* Include padding in width calculation */
}

.time-label {
  position: absolute;
  top: 5px;
  transform: translateX(-50%);
  font-size: 0.7rem;
  color: var(--bui-tbl-row-text, #374151);
  font-weight: 500;
  white-space: nowrap;
  background: var(--bui-tbl-row-fill, rgba(255, 255, 255, 0.9));
  padding: 2px 4px;
  border-radius: 3px;
  border: 1px solid #e5e7eb;
}

.time-label.left {
  left: 0 !important;
  transform: none;
}

.time-label.right {
  left: 100% !important;
  transform: translateX(-100%);
}

.player-rows {
  padding: 0.5rem 0;
}

.player-row {
  position: relative;
  height: 40px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
}

.player-label {
  position: absolute;
  left: 0;
  width: 100px;
  padding: 0 0.5rem;
  font-size: 0.75rem;
  color: var(--bui-tbl-row-text, #374151);
  background: var(--bui-tbl-row-fill, #fffcfd);
  border-right: 1px solid #e5e7eb;
  z-index: 10;
  display: flex;
  align-items: center;
  height: 100%;
  overflow: hidden;
}

.player-link {
  color: var(--bui-tbl-row-link, #3b82f6);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 100%;
}

.player-link:hover {
  color: var(--bui-tbl-row-link-hover, var(--bui-tbl-row-link, #1d4ed8));
  text-decoration: underline;
}

.player-name {
  color: var(--bui-tbl-row-text, #374151);
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 100%;
}

.metric-cells {
  position: relative;
  margin-left: 100px;
  height: 100%;
  width: calc(100% - 100px);
  padding: 0; /* Full width so positions can reach edges */
  overflow: hidden; /* Prevent any overflow */
}

.metric-cell {
  position: absolute;
  height: 20px;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 3px;
  max-width: none; /* Allow exact bucket widths (e.g., 4h = 16.667%) */
  box-sizing: border-box; /* Include padding/border in width calculation */
}

.metric-cell:hover {
  transform: translateY(-50%) scale(1.1);
  z-index: 5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.intensity-low {
  background: #BAFF5C;
}

.intensity-medium {
  background: #00FFFF;
}

.intensity-high {
  background: #DE3DA9;
}

/* Responsive design */
@media (max-width: 768px) {
  .heatmap-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .metric-legend {
    font-size: 0.7rem;
    gap: 0.5rem;
  }
  
  .player-label {
    width: 80px;
    font-size: 0.7rem;
  }
  
  .metric-cells {
    margin-left: 80px;
    width: calc(100% - 80px);
    padding: 0;
  }
  
  .time-axis {
    margin-left: 80px;
    padding: 0;
    width: calc(100% - 80px); /* Match mobile metric cells width */
    overflow: hidden;
    box-sizing: border-box;
  }
}
</style>
